import { PwaUpdateController } from "../../pwa/PwaUpdateController.js";
import __decorate from "../../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PdConfirmDialog } from "@progressive-development/pd-dialog";
//#region src/components/pd-update-prompt/PdUpdatePrompt.ts
/**
* PdUpdatePrompt - Built-in confirmation dialog for service-worker updates.
*
* Rendered by the {@link PdSpaHelper} shell on every route (like the toast
* and the login modal). Watches {@link pwaUpdateService} via
* {@link PwaUpdateController} and shows a {@link PdConfirmDialog} when a new
* version is discovered **during** the session — updates already waiting at
* app start are applied silently by the service itself (autoApplyOnStart).
*
* "Later" dismisses for the running session; the new version then arrives
* automatically on the next app start. Confirm applies the update (single
* controlled reload).
*
* In an app without a service worker `updateAvailable` never becomes true and
* this component renders nothing — safe to mount unconditionally.
*
* Texts default to German and are configurable per app via
* `startInit({ pwaUpdateConfigParam: { texts: {...} } })`; the shell passes
* them through. Apps needing full control (own i18n, custom dialog) override
* `PdSpaHelper._renderUpdatePrompt()` instead.
*/
/**
* Default (German) dialog texts — single source, used by prompt and dialog.
* Deliberately terse: on mobile the dialog must stay small, and the footer
* buttons wrap when they get long. Loss of unsaved input is the one thing
* the text must say.
*/
var PWA_UPDATE_PROMPT_DEFAULT_TEXTS = {
	title: "Neue Version verfügbar",
	hint: "Aktualisieren lädt die Seite neu (aktuelle Eingaben gehen verloren).",
	detail: "Mit „Später“ kommt die neue Version beim nächsten Start.",
	confirm: "Aktualisieren",
	later: "Später"
};
var PdUpdateDialog = class PdUpdateDialog extends PdConfirmDialog {
	get _dialogTitle() {
		return this.dialogTitle;
	}
	constructor() {
		super();
		this.dialogTitle = PWA_UPDATE_PROMPT_DEFAULT_TEXTS.title;
		this.hintText = PWA_UPDATE_PROMPT_DEFAULT_TEXTS.hint;
		this.detailText = PWA_UPDATE_PROMPT_DEFAULT_TEXTS.detail;
		this._dialogType = "info";
		this._singleButton = false;
		this.confirmButtonText = PWA_UPDATE_PROMPT_DEFAULT_TEXTS.confirm;
		this.cancelButtonText = PWA_UPDATE_PROMPT_DEFAULT_TEXTS.later;
	}
	_renderContent() {
		return html`
      ${this.hintText ? html`<p>${this.hintText}</p>` : nothing}
      ${this.detailText ? html`<p>${this.detailText}</p>` : nothing}
    `;
	}
};
__decorate([property({ type: String })], PdUpdateDialog.prototype, "dialogTitle", void 0);
__decorate([property({ type: String })], PdUpdateDialog.prototype, "hintText", void 0);
__decorate([property({ type: String })], PdUpdateDialog.prototype, "detailText", void 0);
PdUpdateDialog = __decorate([customElement("pd-update-dialog")], PdUpdateDialog);
var PdUpdatePrompt = class PdUpdatePrompt extends LitElement {
	constructor(..._args) {
		super(..._args);
		this._update = new PwaUpdateController(this);
		this._declined = false;
	}
	render() {
		if (!this._update.updateAvailable || this._update.applying || this._declined) return nothing;
		const texts = {
			...PWA_UPDATE_PROMPT_DEFAULT_TEXTS,
			...this.texts
		};
		return html`
      <pd-update-dialog
        closeByEscape
        .dialogTitle="${texts.title}"
        .hintText="${texts.hint}"
        .detailText="${texts.detail}"
        .confirmButtonText="${texts.confirm}"
        .cancelButtonText="${texts.later}"
        @pd-dialog-confirm="${this._apply}"
        @pd-dialog-cancel="${this._decline}"
        @popup-close="${this._decline}"
      ></pd-update-dialog>
    `;
	}
	_apply() {
		this._update.apply();
	}
	_decline() {
		this._declined = true;
	}
};
__decorate([property({ attribute: false })], PdUpdatePrompt.prototype, "texts", void 0);
__decorate([state()], PdUpdatePrompt.prototype, "_declined", void 0);
PdUpdatePrompt = __decorate([customElement("pd-update-prompt")], PdUpdatePrompt);
//#endregion
export { PWA_UPDATE_PROMPT_DEFAULT_TEXTS, PdUpdateDialog, PdUpdatePrompt };
